// @contexts/InviteAcceptClient.context.tsx
import React, { createContext, useContext, ReactNode, useMemo } from "react";
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import type { InviteAcceptClient } from "@services/clients/InviteAcceptClient";
import { DefaultInviteAcceptClient } from "@services/clients/DefaultInviteAcceptClient";
import { StubInviteAcceptClient } from "@services/stubs/StubInviteAcceptClient";
import { enabledFeatures, FeaturesEnum } from "@utils/enabledFeatures";
import { useJwtController } from "@contexts/Jwt.context";
import { requireContext } from "@contexts/requireContext";

const InviteAcceptClientContext = createContext<OptionTs<InviteAcceptClient>>(Option.none());

export const InviteAcceptClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { markUnauthorized } = useJwtController();
    const inviteAcceptClient = useMemo<InviteAcceptClient>(() => {
        if (enabledFeatures[FeaturesEnum.StubClients]) {
            return new StubInviteAcceptClient();
        }
        return new DefaultInviteAcceptClient(markUnauthorized);
    }, [markUnauthorized]);

    const contextValue = useMemo(() => Option.some(inviteAcceptClient), [inviteAcceptClient]);

    return (
        <InviteAcceptClientContext.Provider value={contextValue}>
            {children}
        </InviteAcceptClientContext.Provider>
    );
};

export const useInviteAcceptClient = (): InviteAcceptClient => {
    return requireContext(
        useContext(InviteAcceptClientContext),
        "useInviteAcceptClient must be used within an InviteAcceptClientProvider",
    );
};
